<template>
  <a-modal :title="title" :width="1100" :visible="visible" :confirmLoading="confirmLoading" @ok="handleSubmit" @cancel="handleCancel">
    <a-spin :spinning="confirmLoading">
      <a-form-model ref="form" :model="form" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
        <a-form-model-item label="选择商品">
          <a-select
            v-model="selectedSkuIds"
            mode="multiple"
            show-search
            :filter-option="false"
            :default-active-first-option="false"
            :auto-clear-search-value="false"
            @search="handleGoodsSearch"
            @change="handleGoodsChange"
            @dropdownVisibleChange="handleDropdownVisibleChange"
            style="width: 100%"
            placeholder="请选择盘点商品（支持搜索）"
          >
            <template slot="notFoundContent">
              <a-spin v-if="goodsSearchLoading" size="small" />
              <span v-else>暂无数据</span>
            </template>
            <a-select-option v-for="item in goodsOptions" :key="item.skuId" :value="item.skuId">
              {{ item.goodsName }}
              <span v-if="item.skuName" style="color: #666; margin: 0 4px;">【{{ item.skuName }}】</span>
              <span v-if="item.goodsCode" style="color: #999">({{ item.goodsCode }})</span>
              <a-tag v-if="item.stock > 0" color="green" style="margin-left: 8px">库存: {{ item.stock }}</a-tag>
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="盘点备注" prop="remark">
          <a-textarea
            v-model="form.remark"
            placeholder="请输入备注（选填）"
            :rows="2"
            :maxLength="200"
            style="width: 600px"
          />
        </a-form-model-item>
      </a-form-model>

      <!-- 商品盘点数量设置区域 -->
      <div>
        <a-divider>商品盘点数量设置</a-divider>

        <!-- 统一设置区域 -->
        <a-form layout="inline" style="margin-bottom: 16px; background: #f5f5f5; padding: 12px; border-radius: 4px;">
          <a-form-item label="统一设置">
            <a-input-number
              v-model="uniformSettings.actualStock"
              :min="0"
              :precision="0"
              :max="999999"
              placeholder="实际库存"
              style="width: 120px"
            />
            <a-button type="primary" @click="handleUniformSet" style="margin-left: 8px">
              <a-icon type="check" />统一设置
            </a-button>
            <a-button @click="handleResetAll" style="margin-left: 8px">
              <a-icon type="undo" />重置所有
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 商品表格 -->
        <a-table
          :columns="goodsColumns"
          :data-source="selectedGoods"
          :pagination="false"
          size="small"
          :rowKey="'skuId'"
          :scroll="{ y: 400 }"
        >
          <span slot="goodsName" slot-scope="text, record">
            <img v-if="record.picture" :src="record.picture" style="width: 40px; height: 40px; margin-right: 8px; vertical-align: middle;" />
            {{ record.goodsName }}
            <a-tag v-if="record.skuName" color="orange" style="margin-left: 8px">{{ record.skuName }}</a-tag>
          </span>
          <span slot="bookStock" slot-scope="text">
            <a-tag color="blue">{{ text || 0 }}</a-tag>
          </span>
          <span slot="actualStock" slot-scope="text, record">
            <a-input-number
              v-model="record.actualStock"
              :min="0"
              :precision="0"
              :max="999999"
              style="width: 120px"
              placeholder="实际库存"
            />
          </span>
          <span slot="action" slot-scope="text, record">
            <a-button type="link" size="small" @click="removeGoods(record)">
              <a-icon type="delete" />移除
            </a-button>
          </span>
        </a-table>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { createCheckBatch } from '@/api/retail/check'
import { ptPurchaseList } from '@/api/retail/inventory'
import { debounce } from 'lodash'
import storage from 'store'
import { SHOP_ID } from '@/store/mutation-types'

export default {
  name: 'CreateForm',
  data() {
    return {
      visible: false,
      confirmLoading: false,
      title: '创建盘点批次',
      selectedSkuIds: [],
      selectedGoods: [],
      goodsOptions: [],
      goodsSearchLoading: false,
      // 请求序号, 防止先发后到的响应覆盖最新搜索结果
      goodsSearchSeq: 0,
      form: {
        remark: ''
      },
      rules: {},
      goodsColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          scopedSlots: { customRender: 'goodsName' },
          width: 300,
          fixed: 'left'
        },
        {
          title: '账面库存',
          dataIndex: 'bookStock',
          key: 'bookStock',
          scopedSlots: { customRender: 'bookStock' },
          width: 100,
          align: 'center'
        },
        {
          title: '实际库存',
          dataIndex: 'actualStock',
          key: 'actualStock',
          scopedSlots: { customRender: 'actualStock' },
          width: 150,
          align: 'center'
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80,
          align: 'center',
          fixed: 'right'
        }
      ],
      uniformSettings: {
        actualStock: undefined
      }
    }
  },
  created() {
    // 实例级debounce, 避免多实例共享同一个节流函数
    this.handleGoodsSearch = debounce(this.fetchGoods.bind(this), 300)
  },
  methods: {
    // 实时读取门店ID(localStorage非响应式, 不能用computed缓存)
    // 平台账号为0, 表示平台/全部门店, 属于有效值
    getOrgId() {
      const shopId = storage.get(SHOP_ID)
      if (shopId === undefined || shopId === null || shopId === '') return null
      // 雪花ID超出JS安全整数范围(2^53-1), 必须按字符串传递, 禁止Number()转换以免丢精度
      // 例如 670317335210524673 经 Number() 后会变成 670317335210524700
      return String(shopId).trim()
    },
    handleDropdownVisibleChange(visible) {
      if (visible && !this.goodsSearchLoading) {
        if (this.goodsOptions.length === 0) {
          this.loadGoodsByStore()
        }
      }
    },
    handleAdd() {
      this.form.remark = ''
      this.selectedSkuIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.uniformSettings = {
        actualStock: undefined
      }
      this.loadGoodsByStore()
      this.visible = true
    },
    loadGoodsByStore() {
      this.fetchGoods('')
    },
    // 加载/搜索可选商品, keyword为空则加载全部
    fetchGoods(keyword) {
      const orgId = this.getOrgId()
      if (orgId === null) {
        this.$message.warning('无法获取门店ID，请重新登录')
        return
      }
      const kw = (keyword || '').trim()
      // 每次请求递增序号, 过期响应直接丢弃, 避免全量加载覆盖搜索结果
      const seq = ++this.goodsSearchSeq
      this.goodsSearchLoading = true
      ptPurchaseList({
        storeId: orgId,
        goodsName: kw || undefined,
        current: 1,
        size: 1000
      }).then(response => {
        if (seq !== this.goodsSearchSeq) return
        let list = response.data?.records || response.data || []
        if (Array.isArray(list) && kw) {
          const lowerKw = kw.toLowerCase()
          list = list.filter(item => {
            return (item.goodsName && item.goodsName.toLowerCase().includes(lowerKw)) ||
                   (item.goodsCode && item.goodsCode.toLowerCase().includes(lowerKw)) ||
                   (item.skuName && item.skuName.toLowerCase().includes(lowerKw))
          })
        }
        this.goodsOptions = this.mergeSelectedOptions(Array.isArray(list) ? list : [])
        this.goodsSearchLoading = false
      }).catch(() => {
        if (seq !== this.goodsSearchSeq) return
        this.goodsSearchLoading = false
      })
    },
    // 已选商品始终保留在下拉选项中, 避免搜索后选项丢失导致显示裸ID
    mergeSelectedOptions(list) {
      const map = new Map()
      list.forEach(item => map.set(String(item.skuId), item))
      this.selectedGoods.forEach(item => {
        const key = String(item.skuId)
        if (item.skuId !== undefined && item.skuId !== null && !map.has(key)) {
          map.set(key, item)
        }
      })
      return Array.from(map.values())
    },
    handleGoodsChange(value) {
      const existingSkuIds = this.selectedGoods.map(item => item.skuId)
      const newSkuIds = value.filter(id => !existingSkuIds.includes(id))

      if (newSkuIds.length > 0) {
        for (const skuId of newSkuIds) {
          const goodsOption = this.goodsOptions.find(item => item.skuId === skuId)
          if (goodsOption) {
            const exists = this.selectedGoods.find(item => item.skuId === skuId)
            if (!exists) {
              this.selectedGoods.push({
                id: goodsOption.skuId,
                goodsId: goodsOption.goodsId,
                skuId: goodsOption.skuId,
                goodsName: goodsOption.goodsName,
                skuName: goodsOption.skuName || '',
                picture: goodsOption.picture,
                bookStock: goodsOption.stock || 0,
                actualStock: goodsOption.stock || 0,
                shopPrice: goodsOption.shopPrice || 0
              })
            }
          }
        }
      }

      this.selectedGoods = this.selectedGoods.filter(item => {
        return value.includes(item.skuId)
      })
    },
    removeGoods(record) {
      const skuIndex = this.selectedSkuIds.indexOf(record.skuId)
      if (skuIndex > -1) {
        this.selectedSkuIds.splice(skuIndex, 1)
      }

      const index = this.selectedGoods.findIndex(item => item.id === record.id)
      if (index > -1) {
        this.selectedGoods.splice(index, 1)
      }
    },
    handleSubmit() {
      if (this.selectedGoods.length === 0) {
        this.$message.warning('请选择商品')
        return
      }

      const invalidGoods = this.selectedGoods.find(item => {
        return item.actualStock === undefined || item.actualStock === null || item.actualStock < 0
      })

      if (invalidGoods) {
        this.$message.warning('请完善所有商品的实际库存')
        return
      }

      this.confirmLoading = true

      const items = this.selectedGoods.map(item => ({
        goodsId: item.goodsId,
        skuId: item.skuId,
        actualStock: item.actualStock,
        goodsName: item.goodsName,
        skuName: item.skuName,
        shopPrice: item.shopPrice || 0
      }))

      const params = {
        remark: this.form.remark,
        checkItems: items
      }

      createCheckBatch(params).then(() => {
        this.$message.success('盘点批次创建成功')
        this.visible = false
        this.$emit('ok')
      }).catch(error => {
        this.$message.error('盘点批次创建失败：' + (error.message || '未知错误'))
      }).finally(() => {
        this.confirmLoading = false
      })
    },
    handleCancel() {
      this.visible = false
      this.selectedSkuIds = []
      this.selectedGoods = []
      this.form.remark = ''
      this.uniformSettings = {
        actualStock: undefined
      }
    },
    handleUniformSet() {
      if (this.uniformSettings.actualStock === undefined || this.uniformSettings.actualStock === null || this.uniformSettings.actualStock < 0) {
        this.$message.warning('请先设置要统一的实际库存')
        return
      }

      this.selectedGoods.forEach(item => {
        item.actualStock = this.uniformSettings.actualStock
      })

      this.$message.success('统一设置成功')
    },
    handleResetAll() {
      this.$confirm({
        title: '确认重置',
        content: '确定要重置所有商品的实际库存为账面库存吗？',
        onOk: () => {
          this.selectedGoods.forEach(item => {
            item.actualStock = item.bookStock
          })
          this.uniformSettings = {
            actualStock: undefined
          }
          this.$message.success('重置成功')
        }
      })
    }
  }
}
</script>

<style scoped>
:deep(.ant-table) {
  font-size: 13px;
}
</style>

